import { useQuery } from '@tanstack/react-query'

import api from '@/lib/api'
import { useAccessToken, useCurrentUser } from '@/features/auth/state/auth'
import type { StepStatus, WorkflowStep } from '@/types/processador-previdenciario'

export interface PrevidenciarioHistoricoResponse {
  id: string
  etapa: string
  status: string
  origem: string | null
  evento: string | null
  arquivoNome: string | null
  arquivoTamanho: number | null
  statusCode: number | null
  erro: string | null
  detalhes: PrevidenciarioHistoricoDetalhesResponse | null
  createdAt: string
}

export interface PrevidenciarioHistoricoChangeResponse {
  path: string
  kind: 'added' | 'removed' | 'updated'
  before?: unknown
  after?: unknown
}

export interface PrevidenciarioHistoricoDetalhesResponse extends Record<string, unknown> {
  changes?: PrevidenciarioHistoricoChangeResponse[]
  changeCount?: number
}

export interface PrevidenciarioWorkflowStepResponse {
  id: WorkflowStep
  status: StepStatus | 'locked' | 'ready'
  unlocked: boolean
  lastEventAt: string | null
  arquivoNome: string | null
  evento: string | null
  erro: string | null
}

export interface PrevidenciarioWorkflowResponse {
  analysisEmpty: boolean
  currentStep: WorkflowStep | null
  lockedAfterClosing: boolean
  steps: Record<WorkflowStep, PrevidenciarioWorkflowStepResponse>
}

export interface PrevidenciarioDetalhesResponse {
  id: string
  workflow?: PrevidenciarioWorkflowResponse
  historicos?: PrevidenciarioHistoricoResponse[]
  [key: string]: unknown
}

export function useFetchPrevidenciarioDetalhesById(idPrevidenciario?: string) {
  const token = useAccessToken()
  const user = useCurrentUser()

  return useQuery({
    queryKey: ['previdenciario-detalhes', idPrevidenciario],
    queryFn: async (): Promise<PrevidenciarioDetalhesResponse> => {
      if (!idPrevidenciario) {
        throw new Error('ID previdenciário não informado')
      }

      if (!user || !token) {
        throw new Error('Usuário não autenticado')
      }

      const response = await api.get<PrevidenciarioDetalhesResponse>(
        `/previdenciario/detalhes/${idPrevidenciario}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      if (!response.data) {
        throw new Error('Erro ao buscar detalhes do previdenciário')
      }

      return response.data
    },
    enabled: Boolean(idPrevidenciario && user && token)
  })
}
